import {
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
  OnInit,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatOptionModule } from "@angular/material/core";
import { TablerIconsModule } from "angular-tabler-icons";

export interface PaginatorConfig {
  pageIndex: number;
  pageSize: number;
  cursors: (string | null)[];
}

// PaginatorState === PaginatorConfig (hasMorePages is derived from the last cursor)
export type PaginatorState = PaginatorConfig;

@Component({
  selector: "app-treeslo-paginator-cursor-based",
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    TablerIconsModule,
  ],
  templateUrl: "./treeslo-paginator-cursor-based.component.html",
  styleUrls: ["./treeslo-paginator-cursor-based.component.scss"],
})
export class TreesloPaginatorCursorBasedComponent implements OnInit {
  // Inputs for initialization only
  @Input() pageSizeOptions: number[] = [5, 10, 20, 50];
  @Input() paginatorConfig: PaginatorConfig | null = null;
  @Input() pageSizeDisabled = false;

  // Internal state
  private pageIndex = signal(0);
  private pageSize = signal<number>(0);
  private cursors = signal<(string | null)[]>([null]);
  private initialConfigApplied = false;

  ngOnInit(): void {
    if (this.paginatorConfig && !this.initialConfigApplied) {
      this.setState(this.paginatorConfig);
      this.initialConfigApplied = true;
      return;
    }

    if (this.pageSize() === 0) {
      this.pageSize.set(this.pageSizeOptions[0] ?? 10);
    }
  }

  @Output() pageChange = new EventEmitter<{
    pageIndex: number;
    pageSize: number;
    cursor: string | null;
  }>();

  protected get canPrev(): boolean {
    return this.pageIndex() > 0;
  }

  protected get canNext(): boolean {
    // Can go to next page if:
    // 1. A valid cursor (not null, not undefined) exists for the next page, OR
    // 2. There is still more data to load (last cursor is not null)
    const nextPageIndex = this.pageIndex() + 1;
    const nextCursor = this.cursors()[nextPageIndex];
    return (
      (nextCursor !== undefined && nextCursor !== null) ||
      this.calculateHasMorePages()
    );
  }

  protected onPrev(): void {
    if (!this.canPrev) return;
    const newPageIndex = this.pageIndex() - 1;
    this.emitPageChange(newPageIndex);
  }

  protected onNext(): void {
    if (!this.canNext) return;
    const newPageIndex = this.pageIndex() + 1;
    this.emitPageChange(newPageIndex);
  }

  protected onPageSizeChange(value: string): void {
    if (this.pageSizeDisabled) {
      return;
    }

    const newPageSize = Number(value);
    if (newPageSize && newPageSize !== this.pageSize()) {
      this.pageIndex.set(0);
      this.pageSize.set(newPageSize);
      this.cursors.set([null]);
      this.emitPageChange(0, newPageSize);
    }
  }

  private emitPageChange(
    pageIndex: number,
    pageSize: number = this.pageSize(),
  ): void {
    this.pageIndex.set(pageIndex);
    this.pageSize.set(pageSize);

    const cursor = this.cursors()[pageIndex] ?? null;
    this.pageChange.emit({
      pageIndex,
      pageSize,
      cursor,
    });
  }

  /**
   * Get current paginator state (without hasMorePages, which is derived from the last cursor)
   */
  getState(): PaginatorState {
    return {
      pageIndex: this.pageIndex(),
      pageSize: this.pageSize(),
      cursors: [...this.cursors()],
    };
  }

  /**
   * Restore paginator state (for cache)
   */
  setState(state: PaginatorConfig): void {
    this.pageIndex.set(state.pageIndex);
    this.pageSize.set(this.resolvePageSize(state.pageSize));
    this.cursors.set([...state.cursors]);
  }

  private resolvePageSize(size: number): number {
    const options = this.pageSizeOptions;
    if (!options.length) {
      return size || 10;
    }

    if (options.includes(size)) {
      return size;
    }

    return options.reduce((closest, option) =>
      Math.abs(option - size) < Math.abs(closest - size) ? option : closest,
    );
  }

  /**
   * Update cursors after an API request
   * Note: cursor is for the NEXT page, stored at pageIndex + 1
   * hasMorePages is derived automatically from the last cursor
   */
  updateAfterResponse(cursor: string | null): void {
    const pageIndex = this.pageIndex(); // Read from internal state
    const nextPageIndex = pageIndex + 1; // Cursor is for the next page
    const currentCursors = [...this.cursors()];

    if (nextPageIndex >= currentCursors.length) {
      currentCursors.push(cursor);
    } else {
      currentCursors[nextPageIndex] = cursor;
    }

    this.cursors.set(currentCursors);
  }

  /**
   * Reset paginator to default values
   */
  reset(): void {
    this.pageIndex.set(0);
    // Keep the current pageSize chosen by the user.
    this.cursors.set([null]);
  }

  /**
   * Getter for paginator parameters (compatibility)
   */
  get paginatorParams(): { limit: number; cursor: string | null } {
    const cursor = this.cursors()[this.pageIndex()] ?? null;
    return {
      limit: this.pageSize(),
      cursor,
    };
  }

  /**
   * Getter for paginator state — returns full state for context restoration
   */
  get paginatorState(): PaginatorState {
    return this.getState();
  }

  /**
   * Protected getters for the template
   */
  protected get pageIndexValue(): number {
    return this.pageIndex();
  }

  protected get pageSizeValue(): number {
    const current = this.pageSize();
    if (current > 0) {
      return current;
    }

    if (this.paginatorConfig?.pageSize) {
      return this.resolvePageSize(this.paginatorConfig.pageSize);
    }

    return this.pageSizeOptions[0] ?? 10;
  }

  protected get cursorsValue(): (string | null)[] {
    return this.cursors();
  }

  /**
   * Derive hasMorePages from the last stored cursor
   */
  private calculateHasMorePages(): boolean {
    const lastCursor = this.cursors()[this.cursors().length - 1];
    return lastCursor != null;
  }

  protected get hasMorePagesValue(): boolean {
    return this.calculateHasMorePages();
  }
}
